import {
  TenderPermissionEnum,
  TenderPermissionsFragmentDoc,
} from "@/graphql/__generated__/graphql";
import { useFragment } from "@apollo/client/react";

/**
 * Custom hook to retrieve tender permissions from the cache.
 *
 * This hook uses the `useFragment` hook from Apollo Client to automatically
 * subscribe to changes in the Tender permissions data.
 *
 * @param {string | number} tenderID - The tender ID to get permissions for
 * @returns {Object} Object containing permissions array and convenience boolean checks
 *
 * @example
 * const { permissions, canEditDetails, canArchive, canAddService } = useTenderPermissionsData("123");
 *
 * if (canEditDetails) {
 *   // Show edit button
 * }
 */
export const useTenderPermissionsData = (tenderID: string | number) => {
  const { data } = useFragment({
    fragment: TenderPermissionsFragmentDoc,
    from: {
      __typename: "Tender",
      id: Number(tenderID),
    }
  });

  const permissions = data?.permissions ?? [];

  const canManageDocument = permissions.includes(TenderPermissionEnum.ManageDocument);
  const canAddService = permissions.includes(TenderPermissionEnum.AddService);
  const canArchive = permissions.includes(TenderPermissionEnum.ArchiveTender);
  const canUseChatMessage = permissions.includes(TenderPermissionEnum.ChatMessage);
  const canUseDocumentChat = permissions.includes(TenderPermissionEnum.DocumentChat);
  const canEditDetails = permissions.includes(TenderPermissionEnum.EditTenderDetails);
  const canManageSheetAgent = permissions.includes(TenderPermissionEnum.ManageSheetAgent);
  const canManageTask = permissions.includes(TenderPermissionEnum.ManageTask);
  const canOpenCalcGenerator = permissions.includes(TenderPermissionEnum.OpenCalcGenerator);
  const canRequestMarginDecision = permissions.includes(TenderPermissionEnum.RequestMarginDecision);
  const canRequestRecalculation = permissions.includes(TenderPermissionEnum.RequestRecalculation);
  const canDeclareWonLostOutcome = permissions.includes(TenderPermissionEnum.DeclareWonLostOutcome);
  const canView = permissions.includes(TenderPermissionEnum.View);

  return {
    permissions,
    canManageDocument,
    canAddService,
    canArchive,
    canUseChatMessage,
    canUseDocumentChat,
    canEditDetails,
    canManageSheetAgent,
    canManageTask,
    canOpenCalcGenerator,
    canRequestMarginDecision,
    canRequestRecalculation,
    canDeclareWonLostOutcome,
    canView,
    // Helper function for custom checks
    hasPermission: (permission: TenderPermissionEnum) => permissions.includes(permission),
  };
};
